// Minimal history-API router: the room lives in the path (/oengus/event/slug).

import { parseLegacyHash, roomPath } from '../../shared/sources.ts';
import { isStandalone } from './device.ts';
import { prefs } from './prefs.svelte.ts';

const RETURN_KEY = 'sh.returnTo';

class Router {
  path = $state(location.pathname);
  search = $state(location.search);
  params = $derived(new URLSearchParams(this.search));

  constructor() {
    window.addEventListener('popstate', () => this.sync());

    // Old builds kept the room in the hash (#event/slug, #horaro:event/slug).
    const legacy = parseLegacyHash(location.hash);
    if (legacy && location.pathname === '/') {
      this.navigate(roomPath(legacy) + location.search, { replace: true });
      return;
    }
    // Back from tools.skenmy.com sign-in, which always returns to "/".
    const back = sessionStorage.getItem(RETURN_KEY);
    if (back) {
      sessionStorage.removeItem(RETURN_KEY);
      if (location.pathname === '/' && back.startsWith('/')) this.navigate(back, { replace: true });
      return;
    }
    // The installed app always launches at "/": reopen the schedule it was on,
    // as a native app would. "Change schedule" still reaches the landing page.
    const last = prefs.recent[0];
    if (last && location.pathname === '/' && !location.search && isStandalone()) {
      this.navigate(roomPath(last.ref), { replace: true });
    }
  }

  private sync(): void {
    this.path = location.pathname;
    this.search = location.search;
  }

  navigate(url: string, { replace = false } = {}): void {
    // Entries the app pushed are marked, so back() knows there's a page of ours behind them.
    if (replace) history.replaceState(history.state, '', url);
    else history.pushState({ inApp: true }, '', url);
    this.sync();
    window.scrollTo(0, 0);
  }

  /** Goes back if the app got here itself, else to `fallback` (opened from a link or bookmark). */
  back(fallback: string): void {
    if ((history.state as { inApp?: boolean } | null)?.inApp) history.back();
    else this.navigate(fallback, { replace: true });
  }

  /** Updates query parameters in place without adding history entries. */
  setParams(update: Record<string, string | null>): void {
    const merged: Record<string, string> = {
      ...Object.fromEntries(this.params),
      ...update,
    } as Record<string, string>;
    for (const [k, v] of Object.entries(update)) if (v == null) delete merged[k];
    const qs = new URLSearchParams(merged).toString();
    history.replaceState(history.state, '', `${location.pathname}${qs ? `?${qs}` : ''}`);
    this.sync();
  }

  rememberReturn(): void {
    sessionStorage.setItem(RETURN_KEY, location.pathname + location.search);
  }
}

export const router = new Router();
